import React, { useState, useEffect, useCallback } from "react";
import MainLayout from "../Themes/MainLayout";
import {
  Box, Typography, Button, Chip, Tooltip,
  CircularProgress, Alert, Paper, Stack,
  TextField, Table, TableHead, TableBody, TableRow, TableCell,
  Collapse, Pagination, Tab, Tabs, Select, MenuItem, FormControl,
  InputLabel,
} from "@mui/material";
import {
  CheckCircle, Cancel, HourglassEmpty, AccessTime,
  ExpandMore, ExpandLess, Person, CalendarToday, Send, History,
  SwapHoriz, FileDownload, Search, ArrowBackIos, ArrowForwardIos,
} from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import { useCurrentUser } from "../utils/useCurrentUser";
import { normalizeRole } from "../utils/normalizeRole";
import { getStartOfWeek, formatDateRange } from "../utils/dateUtils";
import WeekPicker from "../Components/WeekPicker";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const WEEKDAYS  = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const PAGE_SIZE = 10;

// Convert decimal hours to "Xhr Ymins" display format.
// compact=true → "9h 45m" (chips/cells), false → "9 hrs 45 mins" (headers/totals)
function fmtHrs(decimal, compact = false) {
  const total = Math.round(parseFloat(decimal || 0) * 60);
  const h     = Math.floor(total / 60);
  const m     = total % 60;
  if (compact) {
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
  }
  if (h === 0) return `${m} min${m !== 1 ? "s" : ""}`;
  if (m === 0) return `${h} hr${h !== 1 ? "s" : ""}`;
  return `${h} hr${h !== 1 ? "s" : ""} ${m} min${m !== 1 ? "s" : ""}`;
}

const ROLE_PRIORITY = [
  "System Admin","Admin","HR","Project Manager","Technical Lead",
];

// Color per role
const ROLE_COLOR = {
  "Admin":          "#FF8B00",
  "System Admin":   "#FF8B00",
  "Project Manager":"#6554C0",
  "HR":             "#00875A",
  "Technical Lead": "#0052CC",
};

function highestRole(roles) {
  for (const r of ROLE_PRIORITY) {
    if (roles.includes(r)) return r;
  }
  return roles[0];
}

function normalizeRoles(raw) {
  const arr = Array.isArray(raw) ? raw : [raw || ""];
  return arr.map(r => normalizeRole(r)).filter(Boolean);
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const toLocalStr = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}-${String(dt.getDate()).padStart(2,"0")}`;
};

function deriveCanAct(status, role) {
  const s = (status || "").toLowerCase().trim();
  if (role === "Technical Lead")  return s === "submitted";
  if (role === "Project Manager") return /^pending,\s*1\s*of\s*\d+\s*approved$/.test(s);
  if (role === "HR")              return /^pending,\s*2\s*of\s*\d+\s*approved$/.test(s);
  if (role === "Admin")           return s === "waiting for admin approval";
  if (role === "System Admin")    return s === "waiting for system admin approval";
  return false;
}

function getPendingLabel(status) {
  const s = (status || "").toLowerCase();
  if (s === "submitted")               return "Waiting for TL approval";
  if (s.includes("pending, 1 of"))     return "Waiting for PM approval";
  if (s.includes("pending, 2 of"))     return "Waiting for HR approval";
  if (s.includes("waiting for admin")) return "Waiting for Admin approval";
  if (s.includes("waiting for system"))return "Waiting for System Admin approval";
  return "";
}

// ─── Status chip ──────────────────────────────────────────────────────────────
const STATUS_CFG = {
  Submitted: { color:"info",    icon:<Send           sx={{fontSize:11}}/> },
  Pending:   { color:"warning", icon:<HourglassEmpty sx={{fontSize:11}}/> },
  Approved:  { color:"success", icon:<CheckCircle    sx={{fontSize:11}}/> },
  Rejected:  { color:"error",   icon:<Cancel         sx={{fontSize:11}}/> },
};

function StatusChip({ status }) {
  if (!status) return null;
  const key = Object.keys(STATUS_CFG).find(k =>
    status.toLowerCase().startsWith(k.toLowerCase())
  ) || "Pending";
  const cfg   = STATUS_CFG[key];
  const label = status.length > 26 ? status.slice(0,26)+"…" : status;
  return (
    <Chip size="small" color={cfg.color} icon={cfg.icon} label={label}
      sx={{ fontSize:"0.62rem", height:20, maxWidth:200 }}/>
  );
}

// ─── Approval step dots ───────────────────────────────────────────────────────
function ApprovalSteps({ status, approvalLevel, actingRole }) {
  const lvl   = parseInt(approvalLevel) || 1;
  const steps = lvl >= 3 ? ["TL","PM","HR"] : lvl === 2 ? ["TL","PM"] : ["TL"];
  const s     = (status || "").toLowerCase().trim();
  let done = 0;
  if (s.startsWith("pending, 1 of"))    done = 1;
  else if (s.startsWith("pending, 2 of")) done = 2;
  else if (s === "approved")             done = steps.length;

  return (
    <Stack direction="row" spacing={0.4} alignItems="center">
      {steps.map((step, i) => {
        const isDone    = i < done;
        const isCurrent = i === done && s !== "approved" && s !== "rejected";
        const isMe =
          (step==="TL" && actingRole==="Technical Lead")  ||
          (step==="PM" && actingRole==="Project Manager") ||
          (step==="HR" && actingRole==="HR");
        return (
          <React.Fragment key={step}>
            <Tooltip title={`${step}: ${isDone?"Approved":isCurrent?"Pending":"Waiting"}`}>
              <Box sx={{
                width:18, height:18, borderRadius:"50%", flexShrink:0,
                display:"flex", alignItems:"center", justifyContent:"center",
                bgcolor: isDone?"#36B37E":isCurrent?"#0052CC":"#DFE1E6",
                border: isMe&&isCurrent?"2.5px solid #FF8B00":"none",
              }}>
                {isDone
                  ? <CheckCircle sx={{fontSize:11,color:"#fff"}}/>
                  : <Typography sx={{fontSize:"0.5rem",
                      color:isCurrent?"#fff":"#97A0AF",fontWeight:700}}>
                      {i+1}
                    </Typography>
                }
              </Box>
            </Tooltip>
            {i < steps.length-1 && (
              <Box sx={{width:12,height:2,borderRadius:1,
                bgcolor:isDone?"#36B37E":"#DFE1E6"}}/>
            )}
          </React.Fragment>
        );
      })}
    </Stack>
  );
}

// ─── Breakdown grid ───────────────────────────────────────────────────────────
// leaveDays / holidayDays: { "YYYY-MM-DD": hoursDeducted }
function BreakdownGrid({ timesheetData, weekStart, weekEnd, leaveDays = {}, holidayDays = {} }) {
  if (!timesheetData?.length) return (
    <Typography variant="caption" color="text.secondary"
      sx={{p:1,display:"block"}}>
      No breakdown data available.
    </Typography>
  );

  const ws = new Date(weekStart); ws.setHours(0,0,0,0);
  const we = new Date(weekEnd);   we.setHours(0,0,0,0);
  const dates = [];
  for (let d = new Date(ws); d <= we; d.setDate(d.getDate()+1))
    dates.push(toLocalStr(new Date(d)));

  const taskMap = new Map();
  timesheetData.forEach(e => {
    // Group by task_label (project task) + task_description so rows with the
    // same project task but different descriptions stay separate.
    const label = e.task_label || null;
    const key   = label ? `${label}||${e.task_description}` : e.task_description;
    if (!taskMap.has(key)) taskMap.set(key, { label, description: e.task_description, days: {} });
    taskMap.get(key).days[toLocalStr(new Date(e.timesheet_date))] =
      parseFloat(e.effort_hours || 0);
  });

  const cellSx = {
    px:1, py:0.5, fontSize:"0.72rem", borderBottom:"1px solid #EBECF0",
  };

  // Determine per-date status for column header colouring
  const getDayMeta = (d) => {
    const lh = leaveDays[d]   || 0;
    const hh = holidayDays[d] || 0;
    const isLeave   = lh >= 8;
    const isHalfLeave = lh > 0 && lh < 8;
    const isHoliday = hh >= 8 && !isLeave;
    const isHalfHoliday = hh > 0 && hh < 8 && !isLeave;
    return { isLeave, isHalfLeave, isHoliday, isHalfHoliday, lh, hh };
  };

  return (
    <Box sx={{overflowX:"auto"}}>
      <Table size="small" sx={{minWidth:400}}>
        <TableHead>
          <TableRow sx={{bgcolor:"#F4F5F7"}}>
            <TableCell sx={{...cellSx,fontWeight:700,color:"#5E6C84",minWidth:160}}>
              Task
            </TableCell>
            {dates.map(d => {
              const dt  = new Date(d+"T00:00:00");
              const { isLeave, isHalfLeave, isHoliday, isHalfHoliday, lh, hh } = getDayMeta(d);
              const bgColor = isLeave ? "#FFF7E6"
                : isHalfLeave ? "#FFFBE6"
                : isHoliday  ? "#F3F0FF"
                : isHalfHoliday ? "#F8F5FF"
                : "transparent";
              return (
                <TableCell key={d} align="center"
                  sx={{...cellSx,fontWeight:700,color:"#5E6C84",minWidth:60,
                    bgcolor: bgColor}}>
                  <Typography sx={{fontSize:"0.6rem",fontWeight:700,textTransform:"uppercase"}}>
                    {WEEKDAYS[dt.getDay()]}
                  </Typography>
                  <Typography sx={{fontSize:"0.72rem",fontWeight:800,color:"#172B4D"}}>
                    {dt.getDate()}
                  </Typography>
                  {/* Leave / holiday badge */}
                  {isLeave && (
                    <Tooltip title={`Leave (${fmtHrs(lh, true)})`}>
                      <Typography sx={{fontSize:"0.55rem",color:"#D46B08",fontWeight:700,lineHeight:1.2}}>
                        🏖 Leave
                      </Typography>
                    </Tooltip>
                  )}
                  {isHalfLeave && (
                    <Tooltip title={`Half-day leave (${fmtHrs(lh, true)})`}>
                      <Typography sx={{fontSize:"0.55rem",color:"#D46B08",fontWeight:700,lineHeight:1.2}}>
                        ½ Leave
                      </Typography>
                    </Tooltip>
                  )}
                  {isHoliday && (
                    <Tooltip title={`Public holiday (${fmtHrs(hh, true)})`}>
                      <Typography sx={{fontSize:"0.55rem",color:"#531DAB",fontWeight:700,lineHeight:1.2}}>
                        🎉 Holiday
                      </Typography>
                    </Tooltip>
                  )}
                  {isHalfHoliday && (
                    <Tooltip title={`Half-day holiday (${fmtHrs(hh, true)})`}>
                      <Typography sx={{fontSize:"0.55rem",color:"#531DAB",fontWeight:700,lineHeight:1.2}}>
                        ½ Holiday
                      </Typography>
                    </Tooltip>
                  )}
                </TableCell>
              );
            })}
            <TableCell align="center"
              sx={{...cellSx,fontWeight:700,color:"#5E6C84",borderLeft:"2px solid #DFE1E6"}}>
              Total
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.from(taskMap.entries()).map(([key, { label, description, days }], i) => {
            const total = Object.values(days).reduce((s,h)=>s+h,0);
            return (
              <TableRow key={i} sx={{"&:hover":{bgcolor:"#F8F9FA"}}}>
                <TableCell sx={{...cellSx,color:"#172B4D"}}>
                  {label && (
                    <Typography sx={{
                      display:"inline-block", fontSize:"0.65rem", fontWeight:700,
                      color:"#0052CC", bgcolor:"#DEEBFF", borderRadius:"4px",
                      px:0.75, py:0.1, mb:0.25, lineHeight:1.4,
                    }}>
                      {label}
                    </Typography>
                  )}
                  <Typography sx={{display:"block", fontSize:"0.72rem", color:"#172B4D", lineHeight:1.4}}>
                    {description}
                  </Typography>
                </TableCell>
                {dates.map(d => {
                  const { isLeave, isHoliday } = getDayMeta(d);
                  return (
                    <TableCell key={d} align="center"
                      sx={{...cellSx,
                        bgcolor: isLeave?"#FFF7E6": isHoliday?"#F3F0FF":"transparent"}}>
                      {days[d]
                        ? <Chip label={fmtHrs(days[d], true)} size="small"
                            sx={{fontSize:"0.62rem",height:16,
                              bgcolor: isLeave?"#FFD591": isHoliday?"#D3ADF7":"#DEEBFF",
                              color:   isLeave?"#873800": isHoliday?"#391085":"#0052CC",
                              fontWeight:700}}/>
                        : <Typography sx={{fontSize:"0.68rem",color:"#DFE1E6"}}>—</Typography>
                      }
                    </TableCell>
                  );
                })}
                <TableCell align="center"
                  sx={{...cellSx,fontWeight:700,color:"#172B4D",
                    borderLeft:"2px solid #DFE1E6"}}>
                  {fmtHrs(total, true)}
                </TableCell>
              </TableRow>
            );
          })}
          {/* Daily total row */}
          <TableRow sx={{bgcolor:"#F4F5F7"}}>
            <TableCell sx={{...cellSx,fontWeight:700,color:"#172B4D",
              borderTop:"2px solid #DFE1E6"}}>
              Daily Total
            </TableCell>
            {dates.map(d => {
              const tot = timesheetData
                .filter(e => toLocalStr(new Date(e.timesheet_date)) === d)
                .reduce((s,e) => s+parseFloat(e.effort_hours||0), 0);
              const { isLeave, isHoliday } = getDayMeta(d);
              return (
                <TableCell key={d} align="center"
                  sx={{...cellSx,fontWeight:700,borderTop:"2px solid #DFE1E6",
                    bgcolor: isLeave?"#FFF7E6": isHoliday?"#F3F0FF":"transparent",
                    color:tot>0?"#0052CC":"#97A0AF"}}>
                  {tot>0?fmtHrs(tot, true):"—"}
                </TableCell>
              );
            })}
            <TableCell align="center"
              sx={{...cellSx,fontWeight:700,color:"#0052CC",
                borderLeft:"2px solid #DFE1E6",borderTop:"2px solid #DFE1E6"}}>
              {fmtHrs(timesheetData.reduce((s,e)=>s+parseFloat(e.effort_hours||0),0), true)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
}

// ─── Project sub-row inside an employee group ─────────────────────────────────
function ProjectRow({
  row, index, empId, actingRole, onApprove, onReject, onRemarksChange,
}) {
  const [expanded,  setExpanded]  = useState(false);
  const [breakdown, setBreakdown] = useState(null);
  const [leaveDays,   setLeaveDays]   = useState({});
  const [holidayDays, setHolidayDays] = useState({});
  const [loadingBD, setLoadingBD] = useState(false);

  const s          = (row.approval_status || "").toLowerCase().trim();
  const isApproved = s === "approved";
  const isRejected = s === "rejected";
  const isDone     = isApproved || isRejected;
  const canAct     = row.can_act;

  // Convert MM/DD/YYYY to YYYY-MM-DD
  const toISO = (mmddyyyy) => {
    if (!mmddyyyy) return null;
    const [m, d, y] = mmddyyyy.split("/");
    return `${y}-${m.padStart(2,"0")}-${d.padStart(2,"0")}`;
  };

  const fetchBreakdown = async () => {
    if (breakdown !== null) { setExpanded(e => !e); return; }
    setLoadingBD(true);
    try {
      const weekStart = toISO(row.week_start_date);
      const weekEnd   = toISO(row.week_end_date);

      // Use direct breakdown endpoint — no role/status filter, fetches all
      // daily entries for this employee+project+week regardless of status.
      const bdRes = await fetch(
        `${BASE_URL}/api/timesheetTracker/timesheet/breakdown/` +
        `${encodeURIComponent(row.employee_id)}/${encodeURIComponent(row.proj_id)}/` +
        `${encodeURIComponent(row.bo_id)}/${weekStart}/${weekEnd}`
      );
      setBreakdown(bdRes.ok ? await bdRes.json() : []);

      // Fetch leave and holiday data for the EMPLOYEE being reviewed
      const year = weekStart ? weekStart.split("-")[0] : new Date().getFullYear();

      const [leaveRes, holidayRes] = await Promise.all([
        fetch(`${BASE_URL}/api/leave-request/?bo_id=${row.bo_id}&employee_id=${row.employee_id}`)
          .then(r => r.ok ? r.json() : []).catch(() => []),
        fetch(`${BASE_URL}/api/holidays?bo_id=${row.bo_id}&year=${year}`)
          .then(r => r.ok ? r.json() : []).catch(() => []),
      ]);

      // Build leave map { "YYYY-MM-DD": hoursDeducted }
      const leaveMap = {};
      const approvedLeaves = (Array.isArray(leaveRes) ? leaveRes : []).filter(l => {
        if (l.delete_flag === "Y") return false;
        const st = (l.status || "").toLowerCase().trim();
        return st === "approved" || st.startsWith("cancellation pending");
      });
      const toLocalDate = (s) => {
        if (!s) return null;
        if (/^\d{4}-\d{2}-\d{2}$/.test(String(s))) return String(s);
        const d = new Date(s);
        return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
      };
      // Generate all dates in the week
      const weekDates = [];
      if (weekStart && weekEnd) {
        let cur = new Date(weekStart + "T00:00:00");
        const end = new Date(weekEnd + "T00:00:00");
        while (cur <= end) {
          weekDates.push(`${cur.getFullYear()}-${String(cur.getMonth()+1).padStart(2,"0")}-${String(cur.getDate()).padStart(2,"0")}`);
          cur.setDate(cur.getDate() + 1);
        }
      }
      approvedLeaves.forEach(leave => {
        const startStr = toLocalDate(leave.start_date);
        const endStr   = toLocalDate(leave.end_date) || startStr;
        if (!startStr) return;
        const dtype = (leave.duration_type || "").toLowerCase().trim();
        weekDates.forEach(d => {
          if (dtype === "specific hours" || dtype === "half day") {
            if (d === startStr) leaveMap[d] = (leaveMap[d] || 0) + parseFloat(leave.total_hours || 4);
          } else {
            if (d >= startStr && d <= endStr) leaveMap[d] = (leaveMap[d] || 0) + 8;
          }
        });
      });
      setLeaveDays(leaveMap);

      // Build holiday map { "YYYY-MM-DD": hoursDeducted }
      const holidayMap = {};
      const holidays = Array.isArray(holidayRes) ? holidayRes : holidayRes.data || [];
      holidays.forEach(h => {
        const dateStr = toLocalDate(h.holiday_date);
        if (!dateStr) return;
        weekDates.forEach(d => {
          if (d === dateStr) {
            holidayMap[d] = (holidayMap[d] || 0) + ((h.holiday_type || "Full Day") === "Half Day" ? 4 : 8);
          }
        });
      });
      setHolidayDays(holidayMap);

      setExpanded(true);
    } catch {
      setBreakdown([]);
      setExpanded(true);
    } finally {
      setLoadingBD(false);
    }
  };

  return (
    <Box sx={{
      borderTop:"1px solid #EBECF0",
      bgcolor: isDone ? "#FAFBFC" : canAct ? "#E9F2FF" : "#FAFBFC",
      opacity: isDone ? 0.72 : 1,
    }}>
      {/* Project info row */}
      <Box sx={{
        px:2, py:0.75,
        display:"flex", alignItems:"center", gap:1.5, flexWrap:"wrap",
        borderLeft: canAct&&!isDone ? "3px solid #0052CC" : "3px solid transparent",
      }}>
        {/* Project name */}
        <Typography sx={{
          fontSize:"0.78rem", fontWeight:600, color:"#0052CC",
          flex:"1 1 140px", minWidth:140,
          whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
        }}>
          {row.proj_name}
        </Typography>

        {/* Week */}
        <Stack direction="row" spacing={0.4} alignItems="center" sx={{flexShrink:0}}>
          <CalendarToday sx={{fontSize:12,color:"#5E6C84"}}/>
          <Typography sx={{fontSize:"0.72rem",color:"#5E6C84",whiteSpace:"nowrap"}}>
            {row.week_start_date} – {row.week_end_date}
          </Typography>
        </Stack>

        {/* Hours */}
        <Stack direction="row" spacing={0.4} alignItems="center" sx={{flexShrink:0}}>
          <AccessTime sx={{fontSize:12,color:"#5E6C84"}}/>
          <Typography sx={{fontSize:"0.72rem",fontWeight:700,color:"#172B4D"}}>
            {fmtHrs(row.effort_hours, true)}
          </Typography>
        </Stack>

        {/* Approval steps */}
        <Box sx={{flexShrink:0}}>
          <ApprovalSteps
            status={row.approval_status}
            approvalLevel={row.approval_level_required}
            actingRole={actingRole}
          />
        </Box>

        <Box sx={{flex:1}}/>
        <Stack direction="column" alignItems="flex-end" spacing={0.2} sx={{flexShrink:0}}>
          <StatusChip status={row.approval_status}/>
          {row.last_approver_name && (
            <Typography sx={{fontSize:"0.6rem",color:"#5E6C84",whiteSpace:"nowrap"}}>
              ✓ {row.last_approver_name}
              {row.last_approved_at && (
                <span style={{color:"#97A0AF"}}> · {row.last_approved_at}</span>
              )}
            </Typography>
          )}
        </Stack>
      </Box>

      {/* Action row */}
      {!isDone && canAct && (
        <Box sx={{
          px:2, pb:0.75, pt:0.5,
          display:"flex", alignItems:"center", gap:1,
          borderTop:"1px dashed #C1D4F0",
        }}>
          <TextField
            size="small"
            placeholder="Remarks (optional)"
            value={row.approval_remarks || ""}
            onChange={e => onRemarksChange(index, e.target.value)}
            multiline minRows={1} maxRows={3}
            sx={{
              width:200,
              "& .MuiInputBase-root":{fontSize:"0.72rem",bgcolor:"#fff",borderRadius:1},
              "& textarea":{resize:"none"},
            }}
          />
          <Box sx={{flex:1}}/>
          <Stack direction="row" spacing={0.75}>
            <Button variant="contained" size="small" color="success"
              startIcon={<CheckCircle sx={{fontSize:13}}/>}
              onClick={() => onApprove(row)}
              sx={{textTransform:"none",fontSize:"0.72rem",fontWeight:700,
                py:0.5,px:1.5}}>
              Approve
            </Button>
            <Button variant="outlined" size="small" color="error"
              startIcon={<Cancel sx={{fontSize:13}}/>}
              onClick={() => onReject(row)}
              sx={{textTransform:"none",fontSize:"0.72rem",fontWeight:700,
                py:0.5,px:1.5}}>
              Reject
            </Button>
          </Stack>
        </Box>
      )}

      {/* Waiting label */}
      {!isDone && !canAct && (
        <Box sx={{px:2,pb:0.5}}>
          <Typography sx={{fontSize:"0.62rem",color:"#97A0AF"}}>
            {getPendingLabel(row.approval_status)}
          </Typography>
        </Box>
      )}

      {/* Breakdown toggle */}
      <Box onClick={fetchBreakdown} sx={{
        borderTop:"1px solid #EBECF0", px:2, py:0.4,
        display:"flex", alignItems:"center", cursor:"pointer",
        bgcolor:"#fff", "&:hover":{bgcolor:"#F4F5F7"},
      }}>
        <Typography variant="caption" color="#0052CC" fontWeight={600}
          sx={{flex:1,fontSize:"0.68rem"}}>
          {expanded ? "Hide" : "View"} daily breakdown
        </Typography>
        {loadingBD
          ? <CircularProgress size={12} sx={{color:"#0052CC"}}/>
          : expanded
            ? <ExpandLess sx={{fontSize:14,color:"#5E6C84"}}/>
            : <ExpandMore sx={{fontSize:14,color:"#5E6C84"}}/>
        }
      </Box>
      <Collapse in={expanded}>
        <Box sx={{px:2,py:1.5,bgcolor:"#FAFBFC",borderTop:"1px solid #EBECF0"}}>
          <BreakdownGrid
            timesheetData={breakdown}
            weekStart={row.week_start_date}
            weekEnd={row.week_end_date}
            leaveDays={leaveDays}
            holidayDays={holidayDays}
          />
        </Box>
      </Collapse>
    </Box>
  );
}

// ─── Employee Group Card ──────────────────────────────────────────────────────
// Groups all project rows for the same employee + week under one header.
function EmployeeGroupCard({
  employeeId, employeeName, weekStart, weekEnd, rows, approvals,
  empId, actingRole, onApprove, onReject, onRemarksChange,
}) {
  const [collapsed, setCollapsed] = useState(false);

  const totalHours  = rows.reduce((s, r) => s+parseFloat(r.effort_hours||0), 0);
  const actionCount = rows.filter(r => r.can_act).length;
  const allDone     = rows.every(r => {
    const s = (r.approval_status||"").toLowerCase();
    return s === "approved" || s === "rejected";
  });

  return (
    <Paper elevation={0} sx={{
      border:"1px solid",
      borderColor: actionCount > 0 ? "#0052CC" : "#DFE1E6",
      borderRadius:1.5, overflow:"hidden",
      opacity: allDone ? 0.75 : 1,
    }}>
      {/* Employee header */}
      <Box
        onClick={() => setCollapsed(c => !c)}
        sx={{
          px:1.5, py:1,
          display:"flex", alignItems:"center", gap:1.5, flexWrap:"wrap",
          bgcolor: actionCount > 0 ? "#E9F2FF" : "#F4F5F7",
          cursor:"pointer",
          "&:hover":{ filter:"brightness(0.97)" },
        }}
      >
        {/* Name */}
        <Stack direction="row" spacing={0.75} alignItems="center"
          sx={{flex:"1 1 160px",minWidth:160}}>
          <Person sx={{fontSize:15,color:"#5E6C84",flexShrink:0}}/>
          <Typography sx={{fontSize:"0.82rem",fontWeight:700,color:"#172B4D",
            whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
            {employeeName}
          </Typography>
        </Stack>

        {/* Week range */}
        <Stack direction="row" spacing={0.4} alignItems="center" sx={{flexShrink:0}}>
          <CalendarToday sx={{fontSize:12,color:"#5E6C84"}}/>
          <Typography sx={{fontSize:"0.72rem",color:"#5E6C84",whiteSpace:"nowrap"}}>
            {weekStart} – {weekEnd}
          </Typography>
        </Stack>

        {/* Project count badge */}
        <Chip
          label={`${rows.length} project${rows.length>1?"s":""}`}
          size="small"
          sx={{fontSize:"0.62rem",height:18,bgcolor:"#DFE1E6",color:"#5E6C84",fontWeight:700}}
        />

        {/* Total hours for this week */}
        <Stack direction="row" spacing={0.4} alignItems="center" sx={{flexShrink:0}}>
          <AccessTime sx={{fontSize:12,color:"#5E6C84"}}/>
          <Typography sx={{fontSize:"0.78rem",fontWeight:700,color:"#172B4D"}}>
            {fmtHrs(totalHours)} total
          </Typography>
        </Stack>

        {/* Action needed badge */}
        {actionCount > 0 && (
          <Chip
            label={`${actionCount} need${actionCount===1?"s":""} action`}
            size="small"
            sx={{fontSize:"0.62rem",height:18,bgcolor:"#FF8B00",
              color:"#fff",fontWeight:700}}
          />
        )}

        <Box sx={{flex:1}}/>
        {collapsed
          ? <ExpandMore sx={{fontSize:16,color:"#5E6C84"}}/>
          : <ExpandLess sx={{fontSize:16,color:"#5E6C84"}}/>
        }
      </Box>

      {/* Project sub-rows */}
      <Collapse in={!collapsed}>
        {rows.map((row) => {
          const actualIndex = approvals.findIndex(a =>
            a.employee_id     === row.employee_id &&
            a.proj_id         === row.proj_id &&
            a.week_start_date === row.week_start_date &&
            a.bo_id           === row.bo_id
          );
          return (
            <ProjectRow
              key={`${row.proj_id}-${row.week_start_date}`}
              row={row}
              index={actualIndex}
              empId={empId}
              actingRole={actingRole}
              onApprove={onApprove}
              onReject={onReject}
              onRemarksChange={onRemarksChange}
            />
          );
        })}
      </Collapse>
    </Paper>
  );
}

// ─── History Card (single project row) ───────────────────────────────────────
function HistoryCard({ row }) {
  const [expanded, setExpanded] = useState(false);
  const isApproved = (row.action_status||"").toLowerCase().includes("approved");
  const isRejected = (row.action_status||"").toLowerCase().includes("rejected");

  return (
    <Paper elevation={0} sx={{
      border:"1px solid",
      borderColor: isApproved?"#ABF5D1":isRejected?"#FFBDAD":"#DFE1E6",
      borderLeft:`3px solid ${isApproved?"#36B37E":isRejected?"#FF5630":"#DFE1E6"}`,
      borderRadius:1.5, overflow:"hidden",
    }}>
      {/* Info row */}
      <Box sx={{
        px:1.5, py:1,
        display:"flex", alignItems:"center", gap:1.5, flexWrap:"wrap",
        bgcolor: isApproved?"#E3FCEF":isRejected?"#FFF4F4":"#FAFBFC",
      }}>
        <Stack direction="row" spacing={0.75} alignItems="center"
          sx={{minWidth:140,flex:"1 1 140px"}}>
          <Typography sx={{fontSize:"0.78rem",fontWeight:700,color:"#0052CC",
            whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
            {row.proj_name}
          </Typography>
          <Typography sx={{fontSize:"0.68rem",color:"#5E6C84",flexShrink:0}}>
            · {row.cl_name}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={0.4} alignItems="center" sx={{flexShrink:0}}>
          <AccessTime sx={{fontSize:12,color:"#5E6C84"}}/>
          <Typography sx={{fontSize:"0.72rem",fontWeight:700,color:"#172B4D"}}>
            {parseFloat(row.total_effort_hours||0) > 0 ? fmtHrs(row.total_effort_hours, true) : "—"}
          </Typography>
        </Stack>

        <Typography sx={{fontSize:"0.68rem",color:"#5E6C84",flexShrink:0}}>
          By: <strong>{row.approver_name}</strong>
        </Typography>

        <Box sx={{flex:1}}/>
        <Stack direction="column" alignItems="flex-end" spacing={0.25}>
          <StatusChip status={row.action_status}/>
          <Typography sx={{fontSize:"0.6rem",color:"#97A0AF"}}>
            {row.approval_date}
          </Typography>
        </Stack>
      </Box>

      {row.approval_remarks && (
        <Box sx={{px:1.5,py:0.5,bgcolor:"#FAFBFC",borderTop:"1px solid #EBECF0"}}>
          <Typography sx={{fontSize:"0.68rem",color:"#5E6C84"}}>
            <strong>Remarks:</strong> {row.approval_remarks}
          </Typography>
        </Box>
      )}

      {/* Breakdown toggle */}
      <Box onClick={() => setExpanded(e=>!e)} sx={{
        borderTop:"1px solid #EBECF0", px:1.5, py:0.4,
        display:"flex", alignItems:"center", cursor:"pointer",
        bgcolor:"#fff", "&:hover":{bgcolor:"#F4F5F7"},
      }}>
        <Typography variant="caption" color="#0052CC" fontWeight={600}
          sx={{flex:1,fontSize:"0.68rem"}}>
          {expanded ? "Hide" : "View"} daily breakdown
        </Typography>
        {expanded
          ? <ExpandLess sx={{fontSize:14,color:"#5E6C84"}}/>
          : <ExpandMore sx={{fontSize:14,color:"#5E6C84"}}/>
        }
      </Box>
      <Collapse in={expanded}>
        <Box sx={{p:1.5,bgcolor:"#FAFBFC",borderTop:"1px solid #EBECF0"}}>
          <BreakdownGrid
            timesheetData={row.timesheet_data || []}
            weekStart={row.week_start_date}
            weekEnd={row.week_end_date}
          />
        </Box>
      </Collapse>
    </Paper>
  );
}

// ─── History Employee Group ────────────────────────────────────────────────────
function HistoryEmployeeGroup({ employeeName, weekStart, weekEnd, rows, onDownload }) {
  const [collapsed, setCollapsed] = useState(false);
  const totalHours  = rows.reduce((s,r) => s+parseFloat(r.total_effort_hours||0), 0);
  const allApproved = rows.every(r => (r.action_status||"").toLowerCase().includes("approved"));
  const anyRejected = rows.some(r  => (r.action_status||"").toLowerCase().includes("rejected"));

  return (
    <Paper elevation={0} sx={{
      border:"1px solid",
      borderColor: anyRejected?"#FFBDAD": allApproved?"#ABF5D1":"#DFE1E6",
      borderRadius:1.5, overflow:"hidden",
    }}>
      {/* Header */}
      <Box sx={{
        px:1.5, py:1,
        display:"flex", alignItems:"center", gap:1.5, flexWrap:"wrap",
        bgcolor: anyRejected?"#FFF4F4": allApproved?"#E3FCEF":"#FAFBFC",
      }}>
        <Box onClick={() => setCollapsed(c=>!c)}
          sx={{display:"flex",alignItems:"center",gap:0.75,flex:"1 1 160px",
            minWidth:160,cursor:"pointer"}}>
          <Person sx={{fontSize:15,color:"#5E6C84",flexShrink:0}}/>
          <Typography sx={{fontSize:"0.82rem",fontWeight:700,color:"#172B4D"}}>
            {employeeName}
          </Typography>
        </Box>

        <Stack direction="row" spacing={0.4} alignItems="center"
          onClick={() => setCollapsed(c=>!c)}
          sx={{flexShrink:0,cursor:"pointer"}}>
          <CalendarToday sx={{fontSize:12,color:"#5E6C84"}}/>
          <Typography sx={{fontSize:"0.72rem",color:"#5E6C84",whiteSpace:"nowrap"}}>
            {weekStart} – {weekEnd}
          </Typography>
        </Stack>

        <Chip label={`${rows.length} project${rows.length>1?"s":""}`} size="small"
          onClick={() => setCollapsed(c=>!c)}
          sx={{fontSize:"0.62rem",height:18,bgcolor:"#DFE1E6",color:"#5E6C84",
            fontWeight:700,cursor:"pointer"}}/>

        <Stack direction="row" spacing={0.4} alignItems="center"
          onClick={() => setCollapsed(c=>!c)} sx={{flexShrink:0,cursor:"pointer"}}>
          <AccessTime sx={{fontSize:12,color:"#5E6C84"}}/>
          <Typography sx={{fontSize:"0.75rem",fontWeight:700,color:"#172B4D"}}>
            {fmtHrs(totalHours)} total
          </Typography>
        </Stack>

        <Box sx={{flex:1}} onClick={() => setCollapsed(c=>!c)}/>

        {/* Download CSV */}
        <Tooltip title="Download this employee's timesheet as CSV">
          <Button size="small" variant="outlined"
            startIcon={<FileDownload sx={{fontSize:13}}/>}
            onClick={e => { e.stopPropagation(); onDownload(employeeName, weekStart, weekEnd, rows); }}
            sx={{textTransform:"none",fontSize:"0.7rem",fontWeight:600,
              py:0.25,px:1,borderColor:"#0052CC",color:"#0052CC",
              "&:hover":{bgcolor:"#E9F2FF"}}}>
            CSV
          </Button>
        </Tooltip>

        <Box onClick={() => setCollapsed(c=>!c)} sx={{cursor:"pointer"}}>
          {collapsed
            ? <ExpandMore sx={{fontSize:16,color:"#5E6C84"}}/>
            : <ExpandLess sx={{fontSize:16,color:"#5E6C84"}}/>
          }
        </Box>
      </Box>

      {/* Project sub-cards */}
      <Collapse in={!collapsed}>
        <Stack spacing={0.75} sx={{p:1,bgcolor:"#FAFBFC"}}>
          {rows.map((row,i) => (
            <HistoryCard
              key={`${row.proj_id}-${row.week_start_date}-${i}`}
              row={row}
            />
          ))}
        </Stack>
      </Collapse>
    </Paper>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const TimesheetApproval = () => {
  const { empId, boId, roles } = useCurrentUser();

  // Roles
  const [allRoles,      setAllRoles]      = useState([]);
  const [selectedRole,  setSelectedRole]  = useState(null); // active role switch

  // Download-only mode: user is not an approver but has CSV download access
  // granted via common_variables (TIMESHEET_DOWNLOAD). They see read-only
  // history for their allowed projects and can export CSV, but cannot approve.
  const [downloadOnly,  setDownloadOnly]  = useState(false);

  // UI state
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [alert,         setAlert]         = useState({ type:"", msg:"" });
  const [tab,           setTab]           = useState(0); // 0=Pending 1=History

  // Pending tab
  const [approvals,     setApprovals]     = useState([]);
  const [activeFilter,  setFilter]        = useState("pending");
  const [page,          setPage]          = useState(1);

  // History tab
  const [history,         setHistory]         = useState([]);
  const [historyLoaded,   setHistoryLoaded]   = useState(false);
  const [historyLoading,  setHistoryLoading]  = useState(false);
  const [historyFilter,   setHistoryFilter]   = useState("all");
  const [historySearch,   setHistorySearch]   = useState("");
  const [historyPage,     setHistoryPage]     = useState(1);
  // Single-week navigator (History/Download tab). null = show all weeks;
  // otherwise a Date for the selected week's Sunday. Filtering compares each
  // row's week_start_date against this week.
  const [weekFilter,      setWeekFilter]      = useState(null);
  const [pickerAnchor,    setPickerAnchor]    = useState(null);

  const showAlert = (type, msg) => {
    setAlert({ type, msg });
    setTimeout(() => setAlert({ type:"", msg:"" }), 4000);
  };

  // ── 1. Detect roles ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!empId || !boId) return;
    const detect = async () => {
      try {
        const normalized    = normalizeRoles(roles);
        const isSystemAdmin = normalized.includes("System Admin");
        const isAdmin       = normalized.includes("Admin");

        const params = new URLSearchParams({ page:1, limit:100, boId, empId, roles });
        const res    = await fetch(`${BASE_URL}/clientprojects/paginated?${params}`);
        const result = await res.json();
        const mine   = (result.data || []).filter(p =>
          p.projectmanagerid === empId ||
          p.technicalleadid  === empId ||
          p.hrid             === empId
        );

        const detected = [];
        if (mine.some(p => p.technicalleadid  === empId)) detected.push("Technical Lead");
        if (mine.some(p => p.projectmanagerid === empId)) detected.push("Project Manager");
        if (mine.some(p => p.hrid             === empId)) detected.push("HR");
        if (isSystemAdmin) detected.push("System Admin");
        else if (isAdmin)  detected.push("Admin");

        if (!detected.length) {
          // Not an approver — check for config-driven CSV download access.
          try {
            const accRes = await fetch(
              `${BASE_URL}/api/timesheetTracker/download-access/${empId}`
            );
            const acc = accRes.ok ? await accRes.json() : { projIds: [] };
            if ((acc.projIds || []).length) {
              setDownloadOnly(true);
              setTab(1);          // only History/download view applies
              setLoading(false);
              return;
            }
          } catch {
            // fall through to the standard "not an approver" message
          }
          setError("You are not assigned as an approver on any project.");
          setLoading(false);
          return;
        }

        // Sort by ROLE_PRIORITY for consistent dropdown order
        const sorted = ROLE_PRIORITY.filter(r => detected.includes(r));
        setAllRoles(sorted);
        setSelectedRole(sorted[0]); // default = highest priority
      } catch {
        setError("Failed to detect your approval role.");
        setLoading(false);
      }
    };
    detect();
  }, [empId, boId, roles]);

  // ── 2. Fetch pending for the selected role only ──────────────────────────────
  const fetchApprovals = useCallback(async () => {
    if (!selectedRole || !empId) return;
    setLoading(true);
    try {
      const rows = await fetch(
        `${BASE_URL}/api/timesheetTracker/approvals/` +
        `${encodeURIComponent(selectedRole)}/${empId}`
      )
        .then(r => r.ok ? r.json() : [])
        .catch(() => []);

      const mapped = rows.map(row => ({
        ...row,
        acting_role:      selectedRole,
        can_act:          deriveCanAct(row.approval_status, selectedRole),
        approval_remarks: row.approval_remarks || "",
      }));

      // Actionable rows first, then by date desc
      mapped.sort((a, b) => {
        if (a.can_act !== b.can_act) return a.can_act ? -1 : 1;
        return new Date(b.week_start_date) - new Date(a.week_start_date);
      });

      setApprovals(mapped);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [selectedRole, empId]);

  useEffect(() => {
    if (selectedRole) {
      setApprovals([]);
      setPage(1);
      setFilter("pending");
      fetchApprovals();
    }
  }, [fetchApprovals, selectedRole]);

  // ── 3. Fetch history (lazy, resets when role changes) ───────────────────────
  const fetchHistory = useCallback(async () => {
    if (!empId) return;
    // Approvers need a selected role; download-only users don't.
    if (!downloadOnly && !selectedRole) return;
    setHistoryLoading(true);
    try {
      const url = downloadOnly
        ? `${BASE_URL}/api/timesheetTracker/download-history/${empId}`
        : `${BASE_URL}/api/timesheetTracker/approvals/history/` +
          `${encodeURIComponent(selectedRole)}/${empId}`;
      const res  = await fetch(url);
      const rows = res.ok ? await res.json() : [];
      setHistory(rows);
      setHistoryLoaded(true);
    } catch {
      setHistory([]);
      setHistoryLoaded(true);
    } finally {
      setHistoryLoading(false);
    }
  }, [selectedRole, empId, downloadOnly]);

  // Reset history when role changes
  useEffect(() => {
    setHistoryLoaded(false);
    setHistory([]);
    setHistoryPage(1);
    setHistoryFilter("all");
  }, [selectedRole]);

  useEffect(() => {
    if (tab === 1 && (selectedRole || downloadOnly) && !historyLoaded) fetchHistory();
  }, [tab, selectedRole, downloadOnly, historyLoaded, fetchHistory]);

  // ── 4. Remarks ───────────────────────────────────────────────────────────────
  const handleRemarksChange = (index, val) =>
    setApprovals(prev => {
      const u = [...prev]; u[index].approval_remarks = val; return u;
    });

  // ── 5. Approve ───────────────────────────────────────────────────────────────
  const handleApprove = async (row) => {
    try {
      const res = await fetch(`${BASE_URL}/api/timesheetTracker/approve`, {
        method:"POST",
        headers:{ "Content-Type":"application/json" },
        body: JSON.stringify({
          bo_id:           row.bo_id,
          employee_id:     row.employee_id,
          week_start_date: row.week_start_date,
          proj_id:         row.proj_id,
          approval_status: "approved",
          remarks:         row.approval_remarks,
          approved_by:     empId,
          role:            normalizeRole(selectedRole),
        }),
      });
      if (!res.ok) throw new Error("Approval failed");
      showAlert("success", "Timesheet approved successfully.");
      fetchApprovals();
      if (historyLoaded) setHistoryLoaded(false);
    } catch {
      showAlert("error", "Approval failed. Please try again.");
    }
  };

  // ── 6. Reject ────────────────────────────────────────────────────────────────
  const handleReject = async (row) => {
    if (!row.approval_remarks?.trim()) {
      showAlert("error", "Please add a remark before rejecting.");
      return;
    }
    try {
      const res = await fetch(`${BASE_URL}/api/timesheetTracker/reject`, {
        method:"POST",
        headers:{ "Content-Type":"application/json" },
        body: JSON.stringify({
          bo_id:           row.bo_id,
          employee_id:     row.employee_id,
          week_start_date: row.week_start_date,
          proj_id:         row.proj_id,
          approval_status: "Rejected",
          remarks:         row.approval_remarks,
          rejected_by:     empId,
          role:            normalizeRole(selectedRole),
        }),
      });
      if (!res.ok) throw new Error("Rejection failed");
      showAlert("success", "Timesheet rejected.");
      fetchApprovals();
      if (historyLoaded) setHistoryLoaded(false);
    } catch {
      showAlert("error", "Rejection failed. Please try again.");
    }
  };

  // ── CSV download ─────────────────────────────────────────────────────────────
  const downloadCSV = (employeeName, weekStart, weekEnd, rows) => {
    // Safely wrap a value in quotes and escape internal quotes
    const q = (val) => `"${String(val ?? "").replace(/"/g, '""')}"`;

    const DAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
    const headers = [
      "Employee","Project","Client","Week Start","Week End",
      "Date","Day","Project Task","Task","Hours","Status","Approved By","Approval Date"
    ].map(q).join(",");

    const csvRows = [headers];

    rows.forEach(row => {
      (row.timesheet_data || []).forEach(entry => {
        const d    = new Date(entry.timesheet_date);
        const day  = DAYS[d.getDay()] || "";
        const date = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
        csvRows.push([
          q(employeeName),
          q(row.proj_name),
          q(row.cl_name),
          q(weekStart),
          q(weekEnd),
          q(date),
          q(day),
          q(entry.task_label || ""),
          q(entry.task_description),
          q(parseFloat(entry.effort_hours||0).toFixed(2)),
          q(row.action_status),
          q(row.approver_name),
          q(row.approval_date),
        ].join(","));
      });
    });

    const BOM  = "\uFEFF"; // UTF-8 BOM so Excel opens accented chars correctly
    const blob = new Blob([BOM + csvRows.join("\r\n")], { type:"text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `timesheet_${employeeName.replace(/\s+/g,"_")}_${weekStart.replace(/\//g,"-")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Build history groups (employee + week) ────────────────────────────────────
  const buildHistoryGroups = (rows) => {
    const map = new Map();
    rows.forEach(row => {
      const key = `${row.employee_id}|${row.week_start_date}`;
      if (!map.has(key)) {
        map.set(key, {
          key, employeeId: row.employee_id,
          employeeName: row.employee_name,
          weekStart: row.week_start_date,
          weekEnd:   row.week_end_date,
          rows: [],
        });
      }
      map.get(key).rows.push(row);
    });
    return Array.from(map.values()).sort((a,b) =>
      new Date(b.weekStart) - new Date(a.weekStart) ||
      a.employeeName.localeCompare(b.employeeName)
    );
  };
  const buildEmployeeGroups = (rows) => {
    const map = new Map();
    rows.forEach(row => {
      // Group by employee + week so different-week submissions are separate groups
      const key = `${row.employee_id}|${row.week_start_date}`;
      if (!map.has(key)) {
        map.set(key, {
          groupKey:     key,
          employeeId:   row.employee_id,
          employeeName: row.employee_name,
          weekStart:    row.week_start_date,
          weekEnd:      row.week_end_date,
          rows:         [],
        });
      }
      map.get(key).rows.push(row);
    });
    // Sort: actionable groups first, then by week desc (newest first), then by name
    return Array.from(map.values()).sort((a, b) => {
      const aHas = a.rows.some(r => r.can_act);
      const bHas = b.rows.some(r => r.can_act);
      if (aHas !== bHas) return aHas ? -1 : 1;
      // Newest week first
      const dateDiff = new Date(b.weekStart) - new Date(a.weekStart);
      if (dateDiff !== 0) return dateDiff;
      return a.employeeName.localeCompare(b.employeeName);
    });
  };

  // ── Derived values ────────────────────────────────────────────────────────────
  const pendingCount  = approvals.filter(r => r.can_act).length;
  const filtered      = activeFilter === "pending"
    ? approvals.filter(r => r.can_act)
    : approvals;

  const employeeGroups    = buildEmployeeGroups(filtered);
  const totalGroupPages   = Math.ceil(employeeGroups.length / PAGE_SIZE);
  const paginatedGroups   = employeeGroups.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE);

  // MM/DD/YYYY -> yyyy-mm-dd (string-comparable, no timezone shifts)
  const mdyToISO = (mdy) => {
    if (!mdy) return "";
    const [m, d, y] = mdy.split("/");
    if (!m || !d || !y) return "";
    return `${y}-${m.padStart(2,"0")}-${d.padStart(2,"0")}`;
  };
  // Date -> yyyy-mm-dd (local, no timezone shift)
  const dateToISO = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}-${String(dt.getDate()).padStart(2,"0")}`;
  };
  const weekFilterISO = weekFilter ? dateToISO(getStartOfWeek(weekFilter)) : "";

  const filteredHistory   = history.filter(r => {
    const matchesStatus = historyFilter === "all" || (r.action_status||"").toLowerCase().includes(historyFilter);
    const matchesSearch = !historySearch.trim() ||
      (r.employee_name||"").toLowerCase().includes(historySearch.toLowerCase());
    // Single week: the row's week must match the selected week's Sunday.
    const matchesWeek = !weekFilterISO || mdyToISO(r.week_start_date) === weekFilterISO;
    return matchesStatus && matchesSearch && matchesWeek;
  });
  const historyGroups     = buildHistoryGroups(filteredHistory);
  const historyTotalPages = Math.ceil(historyGroups.length / PAGE_SIZE);
  const paginatedHistory  = historyGroups.slice(
    (historyPage-1)*PAGE_SIZE, historyPage*PAGE_SIZE
  );

  const approvedCount = history.filter(r =>
    (r.action_status||"").toLowerCase().includes("approved")).length;
  const rejectedCount = history.filter(r =>
    (r.action_status||"").toLowerCase().includes("rejected")).length;

  // ── Loading / error screens ───────────────────────────────────────────────────
  if (loading && !allRoles.length) return (
    <MainLayout>
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress sx={{color:"#0052CC"}}/>
      </Box>
    </MainLayout>
  );

  if (error) return (
    <MainLayout>
      <Alert severity="warning" sx={{mt:2}}>{error}</Alert>
    </MainLayout>
  );

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <MainLayout>
      {alert.msg && (
        <Alert severity={alert.type}
          onClose={() => setAlert({type:"",msg:""})} sx={{mb:2}}>
          {alert.msg}
        </Alert>
      )}

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center"
        mb={1.5} flexWrap="wrap" gap={1.5}>
        <Box>
          <Typography variant="h6" fontWeight={700} color="#172B4D">
            {downloadOnly ? "Timesheet Download" : "Timesheet Approvals"}
          </Typography>
          <Typography variant="caption" color="#5E6C84">
            {downloadOnly
              ? "Download timesheets as CSV for your assigned projects"
              : "Review and approve timesheets submitted for your projects"}
          </Typography>
        </Box>

        {/* ── Role Switch Dropdown ── */}
        {allRoles.length > 0 && (
          <Box display="flex" alignItems="center" gap={1}>
            <SwapHoriz sx={{fontSize:18, color:"#5E6C84"}}/>
            <Typography sx={{fontSize:"0.75rem", color:"#5E6C84", fontWeight:600,
              whiteSpace:"nowrap"}}>
              Acting as:
            </Typography>
            {allRoles.length === 1 ? (
              // Only one role — just show a chip, no dropdown needed
              <Chip
                label={allRoles[0]}
                size="small"
                sx={{
                  fontSize:"0.72rem", height:24, fontWeight:700,
                  bgcolor: ROLE_COLOR[allRoles[0]] || "#0052CC",
                  color:"#fff",
                }}
              />
            ) : (
              <FormControl size="small" sx={{minWidth:160}}>
                <Select
                  value={selectedRole || ""}
                  onChange={e => setSelectedRole(e.target.value)}
                  sx={{
                    fontSize:"0.78rem",
                    fontWeight:700,
                    color: ROLE_COLOR[selectedRole] || "#0052CC",
                    bgcolor:"#fff",
                    borderRadius:1.5,
                    "& .MuiOutlinedInput-notchedOutline":{
                      borderColor: ROLE_COLOR[selectedRole] || "#0052CC",
                      borderWidth: 1.5,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline":{
                      borderColor: ROLE_COLOR[selectedRole] || "#0052CC",
                    },
                    "& .MuiSelect-icon":{
                      color: ROLE_COLOR[selectedRole] || "#0052CC",
                    },
                  }}
                >
                  {allRoles.map(r => (
                    <MenuItem key={r} value={r}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{
                          width:8, height:8, borderRadius:"50%", flexShrink:0,
                          bgcolor: ROLE_COLOR[r] || "#0052CC",
                        }}/>
                        <Typography sx={{fontSize:"0.78rem", fontWeight:600}}>
                          {r}
                        </Typography>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {pendingCount > 0 && tab === 0 && (
              <Chip label={`${pendingCount} pending`} size="small"
                sx={{bgcolor:"#FF8B00",color:"#fff",fontWeight:700,
                  fontSize:"0.62rem",height:20}}/>
            )}
          </Box>
        )}
      </Box>

      {/* Tabs — hidden in download-only mode (only History/download applies) */}
      {!downloadOnly && (
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{
          mb:1.5,
          "& .MuiTab-root":{textTransform:"none",fontWeight:600,
            fontSize:"0.82rem",minHeight:36},
          "& .MuiTabs-indicator":{bgcolor:"#0052CC"},
        }}>
          <Tab label={
            <Stack direction="row" spacing={0.75} alignItems="center">
              <span>Pending Approvals</span>
              {pendingCount > 0 && (
                <Chip label={pendingCount} size="small"
                  sx={{bgcolor:"#FF8B00",color:"#fff",fontWeight:700,
                    fontSize:"0.6rem",height:16}}/>
              )}
            </Stack>
          }/>
          <Tab icon={<History sx={{fontSize:16}}/>} iconPosition="start"
            label="History"/>
        </Tabs>
      )}

      {/* ── PENDING TAB ──────────────────────────────────────────────────────── */}
      {tab === 0 && (
        <>
          <Stack direction="row" spacing={1} mb={1.5}>
            <Tooltip title="Timesheets waiting for your action right now">
              <Button size="small"
                variant={activeFilter==="pending"?"contained":"outlined"}
                onClick={() => { setFilter("pending"); setPage(1); }}
                sx={{textTransform:"none",fontWeight:600,fontSize:"0.78rem",
                  ...(activeFilter==="pending"
                    ? {bgcolor:"#0052CC"}
                    : {borderColor:"#DFE1E6",color:"#5E6C84"})}}>
                Needs My Action ({pendingCount})
              </Button>
            </Tooltip>
            <Tooltip title="All pending submissions across your projects">
              <Button size="small"
                variant={activeFilter==="all"?"contained":"outlined"}
                onClick={() => { setFilter("all"); setPage(1); }}
                sx={{textTransform:"none",fontWeight:600,fontSize:"0.78rem",
                  ...(activeFilter==="all"
                    ? {bgcolor:"#0052CC"}
                    : {borderColor:"#DFE1E6",color:"#5E6C84"})}}>
                All on My Projects ({approvals.length})
              </Button>
            </Tooltip>
          </Stack>

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={28} sx={{color:"#0052CC"}}/>
            </Box>
          ) : employeeGroups.length === 0 ? (
            <Paper elevation={0} sx={{p:4,textAlign:"center",
              border:"1px solid #DFE1E6",borderRadius:2}}>
              <CheckCircle sx={{fontSize:36,color:"#36B37E",mb:1}}/>
              <Typography variant="body1" fontWeight={600} color="#172B4D">
                {activeFilter==="pending"?"All caught up!":"No timesheets found."}
              </Typography>
              <Typography variant="caption" color="#5E6C84">
                {activeFilter==="pending"
                  ? "No timesheets are waiting for your approval right now."
                  : "No timesheets have been submitted for your projects yet."}
              </Typography>
            </Paper>
          ) : (
            <>
              <Stack spacing={0.75}>
                {paginatedGroups.map(group => (
                  <EmployeeGroupCard
                    key={group.groupKey}
                    employeeId={group.employeeId}
                    employeeName={group.employeeName}
                    weekStart={group.weekStart}
                    weekEnd={group.weekEnd}
                    rows={group.rows}
                    approvals={approvals}
                    empId={empId}
                    actingRole={selectedRole}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    onRemarksChange={handleRemarksChange}
                  />
                ))}
              </Stack>
              {totalGroupPages > 1 && (
                <Box display="flex" justifyContent="center" alignItems="center"
                  mt={2} gap={1}>
                  <Typography variant="caption" color="#5E6C84">
                    {(page-1)*PAGE_SIZE+1}–
                    {Math.min(page*PAGE_SIZE, employeeGroups.length)} of {employeeGroups.length} employees
                  </Typography>
                  <Pagination count={totalGroupPages} page={page}
                    onChange={(_,v)=>setPage(v)} size="small" color="primary"/>
                </Box>
              )}
            </>
          )}
        </>
      )}

      {/* ── HISTORY TAB ──────────────────────────────────────────────────────── */}
      {tab === 1 && (
        <>
          {/* Filters + Search + Refresh row */}
          <Stack direction="row" spacing={1} mb={1.5} alignItems="center" flexWrap="wrap" gap={1}>
            {[
              { key:"all",      label:`All (${history.length})` },
              { key:"approved", label:`Approved (${approvedCount})` },
              { key:"rejected", label:`Rejected (${rejectedCount})` },
            ].map(f => (
              <Button key={f.key} size="small"
                variant={historyFilter===f.key?"contained":"outlined"}
                onClick={() => { setHistoryFilter(f.key); setHistoryPage(1); }}
                sx={{textTransform:"none",fontWeight:600,fontSize:"0.75rem",
                  ...(historyFilter===f.key
                    ? { bgcolor: f.key==="approved"?"#36B37E":f.key==="rejected"?"#FF5630":"#0052CC" }
                    : { borderColor:"#DFE1E6",color:"#5E6C84" })}}>
                {f.label}
              </Button>
            ))}

            {/* Employee search */}
            <TextField
              size="small"
              placeholder="Search employee…"
              value={historySearch}
              onChange={e => { setHistorySearch(e.target.value); setHistoryPage(1); }}
              InputProps={{
                startAdornment:(
                  <InputAdornment position="start">
                    <Search sx={{fontSize:15,color:"#97A0AF"}}/>
                  </InputAdornment>
                ),
                sx:{fontSize:"0.78rem",bgcolor:"#fff",borderRadius:1},
              }}
              sx={{width:200,"& .MuiOutlinedInput-notchedOutline":{borderColor:"#DFE1E6"}}}
            />

            {/* Single-week navigator: < [ MM/DD/YYYY – MM/DD/YYYY ] > */}
            <Stack direction="row" spacing={0.5} alignItems="center">
              <IconButton size="small"
                onClick={() => {
                  const base = weekFilter || getStartOfWeek(new Date());
                  setWeekFilter(getStartOfWeek(new Date(base.getFullYear(), base.getMonth(), base.getDate() - 7)));
                  setHistoryPage(1);
                }}
                sx={{color:"#5E6C84"}}>
                <ArrowBackIos fontSize="small"/>
              </IconButton>

              <Button variant="outlined" size="small"
                startIcon={<CalendarToday sx={{fontSize:14}}/>}
                onClick={e => setPickerAnchor(e.currentTarget)}
                sx={{textTransform:"none",fontWeight:700,color:"#172B4D",borderColor:"#DFE1E6",
                  bgcolor:"#fff",px:1.5,fontSize:"0.78rem",whiteSpace:"nowrap",
                  "&:hover":{borderColor:"#0052CC",bgcolor:"#F4F5F7"}}}>
                {weekFilter ? formatDateRange(getStartOfWeek(weekFilter)) : "All weeks"}
              </Button>
              <WeekPicker
                weekStart={weekFilter || getStartOfWeek(new Date())}
                onChange={(ws) => { setWeekFilter(ws); setHistoryPage(1); }}
                anchorEl={pickerAnchor}
                onClose={() => setPickerAnchor(null)}
              />

              <IconButton size="small"
                onClick={() => {
                  const base = weekFilter || getStartOfWeek(new Date());
                  setWeekFilter(getStartOfWeek(new Date(base.getFullYear(), base.getMonth(), base.getDate() + 7)));
                  setHistoryPage(1);
                }}
                sx={{color:"#5E6C84"}}>
                <ArrowForwardIos fontSize="small"/>
              </IconButton>

              {weekFilter && (
                <Tooltip title="Show all weeks">
                  <Button size="small" variant="text"
                    onClick={() => { setWeekFilter(null); setHistoryPage(1); }}
                    sx={{minWidth:0,p:0.5,textTransform:"none",fontSize:"0.72rem",color:"#0052CC"}}>
                    All
                  </Button>
                </Tooltip>
              )}
            </Stack>

            <Box sx={{flex:1}}/>
            <Button size="small" variant="text"
              onClick={() => { setHistoryLoaded(false); fetchHistory(); }}
              sx={{textTransform:"none",fontSize:"0.72rem",color:"#5E6C84"}}>
              Refresh
            </Button>
          </Stack>

          {historyLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={28} sx={{color:"#0052CC"}}/>
            </Box>
          ) : historyGroups.length === 0 ? (
            <Paper elevation={0} sx={{p:4,textAlign:"center",
              border:"1px solid #DFE1E6",borderRadius:2}}>
              <History sx={{fontSize:36,color:"#DFE1E6",mb:1}}/>
              <Typography variant="body1" fontWeight={600} color="#172B4D">
                {historySearch || weekFilter
                  ? "No results for the current filters."
                  : "No history yet."}
              </Typography>
              <Typography variant="caption" color="#5E6C84">
                {historySearch || weekFilter
                  ? "Try a different employee or week."
                  : "Approved and rejected timesheets will appear here."}
              </Typography>
            </Paper>
          ) : (
            <>
              <Stack spacing={1}>
                {paginatedHistory.map(group => (
                  <HistoryEmployeeGroup
                    key={group.key}
                    employeeName={group.employeeName}
                    weekStart={group.weekStart}
                    weekEnd={group.weekEnd}
                    rows={group.rows}
                    onDownload={downloadCSV}
                  />
                ))}
              </Stack>
              {historyTotalPages > 1 && (
                <Box display="flex" justifyContent="center" alignItems="center"
                  mt={2} gap={1}>
                  <Typography variant="caption" color="#5E6C84">
                    {(historyPage-1)*PAGE_SIZE+1}–
                    {Math.min(historyPage*PAGE_SIZE, historyGroups.length)} of {historyGroups.length} employees
                  </Typography>
                  <Pagination count={historyTotalPages} page={historyPage}
                    onChange={(_,v) => setHistoryPage(v)}
                    size="small" color="primary"/>
                </Box>
              )}
            </>
          )}
        </>
      )}
    </MainLayout>
  );
};

export default TimesheetApproval;
